import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import Button from '../components/actions/Button'
import useUser from '../contexts/hooks/useUser'
import { ObjectContext } from '../contexts/objectContext/ObjectContext'
import PublicWrapper from '../wrappers/publicWrapper/PublicWrapper'
import ConsumptionElementSlide from './slides/ConsumptionElementSlide'
import ContractTypeSlide from './slides/ContractTypeSlide'
import HeatingTypeSlide from './slides/HeatingTypeSlide'
import ObjectTypeSlide from './slides/ObjectTypeSlide'
import PersonCountSlide from './slides/PersonCountSlide'
import SecondaryHeatingSlide from './slides/SecondaryHeatingSlide'
import UserPreferenceSlide from './slides/UserPreferenceSlide'
import WelcomeSlide from './slides/WelcomeSlide'
import {
	ConsumptionElementSlideStateType,
	ContractSlideStateType,
	HeatingSlideStateType,
	ObjectSlideStateType,
	PersonSlideStateType,
	SecondaryHeatingSlideStateType,
	UserPreferenceSlideStateType,
} from './types/types'

const Onboarding: React.FC = () => {
	const { synerallContractId } = useContext(ObjectContext)

	const { user } = useUser()
	const { t } = useTranslation('pages_onboarding')
	const history = useHistory()

	useEffect(() => {
		if (!user) {
			history.replace('/')
		}
	}, [user, history])

	if (!user) return null

	const handleInteraction = () => {
		setInteractionComplete(prev => {
			const updatedInteractionComplete = [...prev]
			updatedInteractionComplete[currentSlide] = true
			return updatedInteractionComplete
		})
	}

	const objectSlideState = useState<ObjectSlideStateType>(null)
	const personSlideState = useState<PersonSlideStateType>(0)
	const contractSlideState = useState<ContractSlideStateType>({
		contractType: 'spot',
		contractEndDate: null,
		contractEndDateType: null,
		monthlyPrice: '0',
		spotMarginal: '0',
		fixedPrice: '0',
		dayPricePerKwh: '0',
		nightPricePerKwh: '0',
	})
	const heatingSlideState = useState<HeatingSlideStateType>({
		selected: '',
		squares: '',
	})
	const secondaryHeatingSlideState = useState<SecondaryHeatingSlideStateType>([])
	const consumptionElementSlideState = useState<ConsumptionElementSlideStateType>(new Set())
	const userPreferenceSlideState = useState<UserPreferenceSlideStateType>(undefined)

	const [onInteractionFunc, setOnInteractionFunc] = useState<(() => void) | undefined>(undefined)
	const [currentSlide, setCurrentSlide] = useState<number>(0)

	const slides = [
		{ slide: <WelcomeSlide key='welcomeSlide' />, requiresInteraction: false },
		{
			slide: (
				<ObjectTypeSlide
					key='objectTypeSlide'
					onInteraction={handleInteraction}
					state={objectSlideState}
				/>
			),
			requiresInteraction: true,
		},
		{
			slide: (
				<PersonCountSlide
					key='personCountSlide'
					onInteraction={handleInteraction}
					state={personSlideState}
				/>
			),
			requiresInteraction: true,
		},
		{
			slide: (
				<ContractTypeSlide
					key='contractTypeSlide'
					onInteraction={handleInteraction}
					state={contractSlideState}
					setOnInteractionFunc={setOnInteractionFunc}
					onboarding
				/>
			),
			requiresInteraction: false,
			isRendered: !synerallContractId,
		},
		{
			slide: (
				<HeatingTypeSlide
					key='heatingTypeSlide'
					onInteraction={handleInteraction}
					state={heatingSlideState}
				/>
			),
			requiresInteraction: true,
		},
		{
			slide: (
				<SecondaryHeatingSlide key='secondaryHeatingSlide' state={secondaryHeatingSlideState} />
			),
			requiresInteraction: false,
		},
		{
			slide: (
				<ConsumptionElementSlide
					key='consumptionElementSlide'
					state={consumptionElementSlideState}
					setOnInteractionFunc={setOnInteractionFunc}
				/>
			),
			requiresInteraction: false,
		},
		{
			slide: <UserPreferenceSlide key='userPreferenceSlide' state={userPreferenceSlideState} />,
			requiresInteraction: true,
		},
	].filter(slide => slide.isRendered !== false)

	const [interactionComplete, setInteractionComplete] = useState<boolean[]>([])

	useEffect(() => {
		setInteractionComplete(slides.map(e => !e.requiresInteraction))
	}, [synerallContractId])

	const onNext = () => {
		if (onInteractionFunc) {
			onInteractionFunc()
			setOnInteractionFunc(undefined)
		}
		setCurrentSlide(currentSlide + 1)
	}

	const onPrev = () => {
		if (onInteractionFunc) {
			onInteractionFunc()
			setOnInteractionFunc(undefined)
		}

		setCurrentSlide(currentSlide - 1)
	}

	return (
		<PublicWrapper id='onboarding'>
			<div className='question-container mt-4'>
				<div className='question-pager'>
					<div className='question-number tracking-widest dark:text-brand-primary-200-dark dark:bg-brand-secondary-100-dark bg-brand-secondary-200-light text-brand-primary-100-light'>
						{currentSlide + 1}/{slides.length}
					</div>
				</div>
				<div className='question-content flex flex-col items-center justify-center gap-sm'>
					{slides[currentSlide].slide}
					{currentSlide < slides.length - 1 && (
						<Button className='m-0' onClick={onNext} disabled={!interactionComplete[currentSlide]}>
							{t('seuraava', { ns: 'common' })}
						</Button>
					)}
					{currentSlide > 0 && (
						<Button
							className='hollow text-brand-primary-100-light dark:text-brand-secondary-100-dark m-0'
							onClick={onPrev}
						>
							{t('edellinen', { ns: 'common' })}
						</Button>
					)}
				</div>
			</div>
		</PublicWrapper>
	)
}

export default Onboarding
